import { useState, useRef, useCallback } from 'react'
import styles from './ImportModal.module.css'

interface ImportModalProps {
    onImport: (text: string) => void
    onClose: () => void
}

export function ImportModal({ onImport, onClose }: ImportModalProps) {
    const [text, setText] = useState('')
    const [fileName, setFileName] = useState<string | null>(null)
    const [dragging, setDragging] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const readFile = useCallback((file: File) => {
        const reader = new FileReader()
        reader.onload = (e) => {
            const content = e.target?.result as string
            if (content) {
                setText(content)
                setFileName(file.name)
            }
        }
        reader.readAsText(file)
    }, [])

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) readFile(file)
    }

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault()
        setDragging(false)
        const file = e.dataTransfer.files[0]
        if (file) readFile(file)
    }

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault()
        setDragging(true)
    }

    const handleDragLeave = () => setDragging(false)

    return (
        <div className={styles.overlay} onClick={onClose}>
            <div className={styles.modal} onClick={e => e.stopPropagation()}>
                <div className={styles.header}>
                    <span>Import EN Source</span>
                    <button className={styles.close} onClick={onClose}>{'\u2715'}</button>
                </div>

                <div
                    className={`${styles.dropZone} ${dragging ? styles.dropZoneActive : ''}`}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onClick={() => fileInputRef.current?.click()}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".en,.txt"
                        onChange={handleFileSelect}
                        className={styles.fileInput}
                    />
                    {fileName ? (
                        <span className={styles.fileName}>{fileName}</span>
                    ) : (
                        <>
                            <svg className={styles.dropIcon} width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="7 10 12 15 17 10" />
                                <line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                            <span className={styles.dropLabel}>Drop a .en or .txt file, or click to browse</span>
                        </>
                    )}
                </div>

                <div className={styles.divider}><span>or paste text</span></div>

                <textarea
                    className={styles.textarea}
                    placeholder="Paste EN source text here..."
                    value={text}
                    onChange={e => { setText(e.target.value); setFileName(null) }}
                    autoFocus
                />
                <div className={styles.actions}>
                    <button className={styles.btnSecondary} onClick={onClose}>Cancel</button>
                    <button
                        className={styles.btnPrimary}
                        onClick={() => { if (text.trim()) onImport(text.trim()) }}
                        disabled={!text.trim()}
                    >
                        Import
                    </button>
                </div>
            </div>
        </div>
    )
}
